import { useLayoutEffect, useRef, useEffect } from 'react'
import { useSnapshot } from 'valtio'
import { useResponsive } from 'ahooks'
import { Button, Divider } from 'antd'

import ChatItem from './ChatItem'
import InitMessage from './InitMessage'
import ChatTrigger from './ChatTrigger'

import { chatState, chatAction, DeepThinkMode } from '@/store'
import { SessionStatuTypes } from '@/constants'
import { userAgreement, privacyPolicy } from '@/components/Agreement'
import useDownloadApp from '@/hooks/useDownloadApp'
import { RoleTypes } from '@/constants/index'
import styles from './content.module.less'

export const Footer = () => {
  const responsive = useResponsive()

  return (
    <div className="text-center text-xs text-gray-400 py-3">
      {responsive.md ? (
        <>
          <span className="pr-5">内容由AI生成，仅供参考</span>
          <span
            className="cursor-pointer text-brand after:content-['|'] after:mx-2 after:text-gray-400"
            rel="noopener noreferrer"
            onClick={() => {
              window.open(userAgreement)
            }}
          >
            用户协议
          </span>
          <span
            className="cursor-pointer text-brand"
            rel="noopener noreferrer"
            onClick={() => {
              window.open(privacyPolicy)
            }}
          >
            隐私政策
          </span>
        </>
      ) : (
        <span>内容由AI生成，仅供参考</span>
      )}
    </div>
  )
}

const ChatContent = () => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const { sessionMessages, sessionStatu, userScrolled } = useSnapshot(chatState)
  // 记录用户是否手动滚动（向上滚动超过50px）
  const userScrolledRef = useRef(false)
  // 记录上一次的scrollTop，用于检测滚动方向
  const lastScrollTopRef = useRef(0)
  // 记录用户开始滚动时的初始scrollTop
  const initialScrollTopRef = useRef(0)
  // 记录上一次消息长度，用于检测新消息
  const lastMessagesLengthRef = useRef(0)

  const responsive = useResponsive()
  const { launchApp } = useDownloadApp()
  useEffect(() => {
    if (userScrolled) {
      userScrolledRef.current = false
    }
  }, [userScrolled])
  // 监听用户滚动
  useEffect(() => {
    const initScroll = () => {
      const dom = scrollRef.current
      if (!dom) {
        requestAnimationFrame(initScroll)
        return
      }

      let lastScrollTop = dom.scrollTop
      let lastScrollDirection: 'up' | 'down' | null = null
      lastScrollTopRef.current = lastScrollTop
      initialScrollTopRef.current = lastScrollTop

      const handleScroll = () => {
        if (!dom) return
        const currentScrollTop = dom.scrollTop
        const distanceToBottom =
          dom.scrollHeight - currentScrollTop - dom.clientHeight

        // 检测滚动方向：向上滚动
        const isScrollingUp = currentScrollTop < lastScrollTop
        const currentScrollDirection = isScrollingUp ? 'up' : 'down'

        // 当滚动方向从向下变为向上时，记录初始位置
        if (lastScrollDirection === 'down' && currentScrollDirection === 'up') {
          initialScrollTopRef.current = lastScrollTop
        }

        if (isScrollingUp) {
          // 计算用户向上滚动的距离（从初始位置开始）
          const scrollUpDistance =
            initialScrollTopRef.current - currentScrollTop
          // console.log('userScrolled :>> ', userScrolled)
          if (userScrolled) {
            chatAction.updateUserScrolled(false)
            return
          }
          // 如果向上滚动超过50px，标记为用户手动滚动
          if (scrollUpDistance > 50) {
            userScrolledRef.current = true
            // console.log('用户向上滚动超过50px，停止自动滚动', scrollUpDistance)
          }
        } else {
          // 向下滚动时，重置初始位置和用户滚动状态
          initialScrollTopRef.current = currentScrollTop
          // 如果滚动到底部，重置用户滚动状态
          if (distanceToBottom < 10) {
            userScrolledRef.current = false
            // console.log('滚动到底部，重置自动滚动状态')
          }
        }

        lastScrollTop = currentScrollTop
        lastScrollDirection = currentScrollDirection
        lastScrollTopRef.current = currentScrollTop
      }

      // 使用 passive: true 提高滚动性能
      dom.addEventListener('scroll', handleScroll, { passive: true })
      // 初始化时检查一次滚动位置
      handleScroll()

      return () => {
        dom.removeEventListener('scroll', handleScroll)
      }
    }

    const cleanup = initScroll()
    return () => {
      if (cleanup) cleanup()
    }
  }, [userScrolled]) // 确保只在组件挂载时添加监听器

  // 处理消息更新时的滚动
  useLayoutEffect(() => {
    const dom = scrollRef.current
    if (!dom) return

    // 检查消息长度是否发生变化（新消息）
    if (sessionMessages.length !== lastMessagesLengthRef.current) {
      // console.log('消息长度变化，从', lastMessagesLengthRef.current, '变为', sessionMessages.length)
      // 消息长度发生变化，如果用户没有手动滚动，则自动滚动到底部
      userScrolledRef.current = false
      dom.scrollTop = dom.scrollHeight
      initialScrollTopRef.current = dom.scrollTop
      lastScrollTopRef.current = dom.scrollTop
      // 更新上一次的消息长度
      lastMessagesLengthRef.current = sessionMessages.length
      return
    }

    // 如果正在响应中
    if (sessionStatu === SessionStatuTypes.pending) {
      // 如果用户没有手动滚动，则自动滚动到底部
      if (!userScrolledRef.current) {
        dom.scrollTop = dom.scrollHeight
        initialScrollTopRef.current = dom.scrollTop
        lastScrollTopRef.current = dom.scrollTop
        // console.log('pending状态，自动滚动到底部')
      } else {
        // console.log('pending状态，用户已手动滚动，保持当前位置')
      }
    } else {
      if (!userScrolledRef.current) {
        // 如果不在响应中，自动滚动到底部
        dom.scrollTop = dom.scrollHeight
        // 重置用户滚动状态
        userScrolledRef.current = false
        initialScrollTopRef.current = dom.scrollTop
        lastScrollTopRef.current = dom.scrollTop
        // console.log('非pending状态，自动滚动到底部并重置状态')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionMessages, sessionStatu])

  return (
    <div className={styles.content}>
      {!responsive.sm && (
        <div className={styles.donwloadApp}>
          <div className={styles.title}>前往紫东太初APP解锁更多功能</div>
          <div>
            <Button type="primary" onClick={launchApp}>
              下载
            </Button>
          </div>
        </div>
      )}
      {responsive.sm && (
        <div className="h-6 absolute inset-0 -top-10 bg-gradient-to-b from-white to-white/50 blur-sm z-10" />
      )}
      <div ref={scrollRef} className={styles.scrollView}>
        <div className={styles.scrollViewContent}>
          {!sessionMessages.length && <InitMessage />}
          {sessionMessages.map((item: any, index: number) => (
            // 重新生成切换答案需要根据answer_id重新渲染
            <ChatItem
              key={
                (item.role === RoleTypes.user
                  ? item.question_id || item.id
                  : item.answer_id || item.id) || index
              }
              item={item}
              isLast={index === sessionMessages.length - 1}
            />
          ))}
        </div>
      </div>
      {chatState.sessionStatu === SessionStatuTypes.risk ? (
        <div className={styles.refuse}>
          <Divider>
            换个话题重新开始吧,
            <Button
              type="link"
              size="large"
              onClick={() => {
                chatAction.createNewSession()
              }}
            >
              新建对话
            </Button>
          </Divider>
        </div>
      ) : (
        <ChatTrigger />
      )}
      <Footer />
    </div>
  )
}

export default ChatContent
